/**
 * Reflex: train and serve small text classifiers.
 *
 *   const morph = new MorphClient({ apiKey });
 *   const job   = await morph.reflex.jobs.create({ trainingData: rows, suffix: 'support' });
 *   const ready = await morph.reflex.jobs.waitForReady(job.id);
 *   const out   = await morph.reflex.predict({ model: ready.fineTunedModel!, text: 'refund please' });
 *
 * Mirrors the OpenAI fine-tuning shape (`reflex.jobs.create/retrieve/list/cancel/delete`)
 * plus `reflex.predict`. All HTTP goes through the shared `MorphAPIClient` transport.
 */
import { MorphAPIClient } from '../../core/client.js';
import { APIResource } from '../../core/resource.js';
import { MorphError } from '../utils/resilience.js';
import type {
  CreateReflexJobInput,
  DeletedReflexJob,
  ListReflexJobsInput,
  ReflexClassification,
  ReflexConfig,
  ReflexEvent,
  ReflexJob,
  ReflexJobList,
  ReflexPrediction,
  ReflexPredictClass,
  ReflexPredictInput,
  ReflexPredictManyInput,
  ReflexPredictManyResult,
  ReflexPredictResult,
} from './types.js';

const BASE_MODEL = 'morph-reflex-v1';

/** Build a transport from either a shared client or a standalone `ReflexConfig`. */
function resolveClient(clientOrConfig: MorphAPIClient | ReflexConfig): MorphAPIClient {
  if (clientOrConfig instanceof MorphAPIClient) return clientOrConfig;
  return new MorphAPIClient({
    apiKey: clientOrConfig.apiKey,
    baseURL: clientOrConfig.baseUrl,
    timeout: clientOrConfig.timeout,
    retryConfig: clientOrConfig.retryConfig,
    debug: clientOrConfig.debug,
  });
}

/**
 * @deprecated Prefer the unified `MorphClient` (`new MorphClient({ apiKey }).reflex`).
 * Standalone clients remain only for backwards compatibility and may be removed in a future
 * major version — do not use them in new code.
 */
export class ReflexClient extends APIResource {
  /** Train, retrieve, and manage classifier jobs. */
  public readonly jobs: ReflexJobsResource;

  constructor(clientOrConfig: MorphAPIClient | ReflexConfig = {}) {
    super(resolveClient(clientOrConfig));
    this.jobs = new ReflexJobsResource(this._client);
  }

  /** Classify text against one trained model. The model must be `succeeded`. */
  async predict(input: ReflexPredictInput): Promise<ReflexPredictResult> {
    const completionId = input.completionId ?? crypto.randomUUID();
    const raw = await this._client.post<RawPredict>('/v1/reflex/predict', {
      body: { model: input.model, text: input.text },
      headers: { 'X-Completion-Id': completionId },
    });
    return {
      ...toClassification(raw),
      inferenceTimeMs: raw.inference_time_ms,
      prefillTokens: raw.prefill_tokens ?? 0,
      completionId,
    };
  }

  /**
   * Classify text against several models in one request. They share a single
   * prefill, so the input is tokenized once — cheaper and faster than a call per
   * model. Each model returns its own prediction (or an `error` if it failed).
   */
  async predictMany(input: ReflexPredictManyInput): Promise<ReflexPredictManyResult> {
    const completionId = input.completionId ?? crypto.randomUUID();
    const raw = await this._client.post<RawPredictMany>('/v1/reflex/predict', {
      body: { models: input.models, text: input.text },
      headers: { 'X-Completion-Id': completionId },
    });
    return {
      predictions: (raw.predictions ?? []).map(toPrediction),
      inferenceTimeMs: raw.inference_time_ms,
      prefillTokens: raw.prefill_tokens ?? 0,
      completionId,
    };
  }
}

export class ReflexJobsResource extends APIResource {
  /** Start a training job from labeled data, a description, or unlabeled text. */
  async create(input: CreateReflexJobInput): Promise<ReflexJob> {
    return toReflexJob(await this._client.post<RawJob>('/v1/fine_tuning/jobs', { body: createBody(input) }));
  }

  /** Fetch a job by id. */
  async retrieve(id: string): Promise<ReflexJob> {
    return toReflexJob(await this._client.get<RawJob>(`/v1/fine_tuning/jobs/${encodeURIComponent(id)}`));
  }

  /** List the caller's jobs, newest first. */
  async list(input: ListReflexJobsInput = {}): Promise<ReflexJobList> {
    const raw = await this._client.get<RawJobList>('/v1/fine_tuning/jobs', {
      query: { limit: input.limit, after: input.after },
    });
    return { data: (raw.data ?? []).map(toReflexJob), hasMore: Boolean(raw.has_more) };
  }

  /** Stop a queued or running job. */
  async cancel(id: string): Promise<ReflexJob> {
    return toReflexJob(await this._client.post<RawJob>(`/v1/fine_tuning/jobs/${encodeURIComponent(id)}/cancel`));
  }

  /** Delete a job and its trained model. */
  async delete(id: string): Promise<DeletedReflexJob> {
    const raw = await this._client.delete<{ id: string; deleted?: boolean }>(
      `/v1/fine_tuning/jobs/${encodeURIComponent(id)}`,
    );
    return { id: raw.id, deleted: Boolean(raw.deleted) };
  }

  /** The training loss curve as events, plus a terminal event. */
  async events(id: string): Promise<ReflexEvent[]> {
    const raw = await this._client.get<{ data?: RawEvent[] }>(
      `/v1/fine_tuning/jobs/${encodeURIComponent(id)}/events`,
    );
    return (raw.data ?? []).map(toReflexEvent);
  }

  /**
   * Poll until the job reaches a terminal status and return it on success.
   * Throws a `MorphError` if it fails, is cancelled, or exceeds `timeoutMs`.
   */
  async waitForReady(id: string, opts: { pollMs?: number; timeoutMs?: number } = {}): Promise<ReflexJob> {
    const pollMs = opts.pollMs ?? 3_000;
    const deadline = Date.now() + (opts.timeoutMs ?? 15 * 60_000);

    for (;;) {
      const job = await this.retrieve(id);
      if (job.status === 'succeeded') return job;
      if (job.status === 'failed' || job.status === 'cancelled') {
        throw new MorphError(job.error?.message ?? `Reflex job ${job.status}`, `reflex_job_${job.status}`);
      }
      if (Date.now() >= deadline) {
        throw new MorphError(`Reflex job ${id} did not finish in time`, 'reflex_timeout');
      }
      await sleep(pollMs);
    }
  }
}

// ---------------------------------------------------------------------------
// Mapping (camelCase <-> snake_case) and helpers
// ---------------------------------------------------------------------------

interface RawJob {
  id: string;
  model: string;
  created_at: number;
  finished_at: number | null;
  fine_tuned_model: string | null;
  status: ReflexJob['status'];
  labels?: string[];
  trained_examples?: number;
  result?: { accuracy: number | null; f1_score: number | null } | null;
  error?: { message: string } | null;
  suffix?: string | null;
}
interface RawJobList {
  data?: RawJob[];
  has_more?: boolean;
}
interface RawPredictClass {
  class_id: number;
  label: string;
  score: number;
  selected: boolean;
}
interface RawPredict {
  model: string;
  mode?: string;
  classes?: RawPredictClass[];
  inference_time_ms: number;
  prefill_tokens?: number;
}
interface RawPredictionEntry {
  model: string;
  mode?: string;
  classes?: RawPredictClass[];
  error?: { message: string } | string;
}
interface RawPredictMany {
  predictions?: RawPredictionEntry[];
  inference_time_ms: number;
  prefill_tokens?: number;
}
interface RawEvent {
  id: string;
  created_at: number;
  level: ReflexEvent['level'];
  message: string;
  type: ReflexEvent['type'];
  data?: { epoch?: number; step?: number; train_loss?: number };
}

function createBody(input: CreateReflexJobInput): Record<string, unknown> {
  const base: Record<string, unknown> = { model: BASE_MODEL };
  if (input.suffix) base.suffix = input.suffix;

  if ('trainingData' in input) {
    if (input.labels) base.labels = input.labels;
    return { ...base, training_data: input.trainingData };
  }
  if ('generate' in input) {
    return {
      ...base,
      labels: input.labels,
      generate: {
        description: input.generate.description,
        ...(input.generate.examplesPerLabel != null ? { examples_per_label: input.generate.examplesPerLabel } : {}),
      },
    };
  }
  return {
    ...base,
    labels: input.labels,
    label_data: {
      texts: input.labelData.texts,
      ...(input.labelData.description ? { description: input.labelData.description } : {}),
    },
  };
}

function toReflexJob(raw: RawJob): ReflexJob {
  return {
    id: raw.id,
    object: 'fine_tuning.job',
    model: raw.model,
    createdAt: raw.created_at,
    finishedAt: raw.finished_at ?? null,
    fineTunedModel: raw.fine_tuned_model ?? null,
    status: raw.status,
    labels: raw.labels ?? [],
    trainedExamples: raw.trained_examples ?? 0,
    result: raw.result ? { accuracy: raw.result.accuracy ?? null, f1Score: raw.result.f1_score ?? null } : null,
    error: raw.error ? { message: raw.error.message } : null,
    suffix: raw.suffix ?? null,
  };
}

/** Map a raw classification envelope (`{ model, mode, classes }`) to the shared shape. */
function toClassification(raw: { model: string; mode?: string; classes?: RawPredictClass[] }): ReflexClassification {
  const classes: ReflexPredictClass[] = (raw.classes ?? []).map(c => ({
    classId: c.class_id,
    label: c.label,
    score: c.score,
    selected: c.selected,
  }));
  const selectedClasses = classes.filter(c => c.selected);
  // Top selected class by score derives label/confidence.
  const top = selectedClasses.reduce<ReflexPredictClass | null>(
    (best, c) => (best === null || c.score > best.score ? c : best),
    null,
  );
  return {
    model: raw.model,
    mode: raw.mode === 'multi_label' ? 'multi_label' : 'single_label',
    classes,
    selected: selectedClasses.map(c => c.label),
    label: top ? top.label : null,
    confidence: top ? top.score : null,
  };
}

/** Map one `predictMany` entry: a classification, or an `error` if that model failed. */
function toPrediction(raw: RawPredictionEntry): ReflexPrediction {
  const error = raw.error
    ? { message: typeof raw.error === 'string' ? raw.error : raw.error.message }
    : null;
  return { ...toClassification(raw), error };
}

function toReflexEvent(raw: RawEvent): ReflexEvent {
  return {
    id: raw.id,
    createdAt: raw.created_at,
    level: raw.level,
    message: raw.message,
    type: raw.type,
    data: { epoch: raw.data?.epoch ?? 0, step: raw.data?.step ?? 0, trainLoss: raw.data?.train_loss ?? 0 },
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
